import { Card, Col, ListGroup, Row, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { BackButton } from '../components/BackButton';
import { fetchOrder } from '../api/orderApi';
import { formatCurrency, formatDate } from '../utils/formatters';

export function OrderDetailPage({ kind }) {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['order', kind, orderId],
    queryFn: fetchOrder,
  });

  if (isLoading) {
    return (
      <div className="page-container text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading order details...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="page-container">
        <Alert variant="danger">
          Error loading order: {error.message}
        </Alert>
      </div>
    );
  }

  const order = data?.order;
  const pickings = data?.pickings;

  if (!order) {
    return (
      <div className="page-container">
        <Alert variant="warning">Order not found.</Alert>
      </div>
    );
  }

  const lineRows = order.lines.map((line) => [
    line.product_id.name,
    '', // Description - not explicitly in the example data lines but used in old code
    line.product_qty,
    formatCurrency(line.price_unit),
    formatCurrency(line.price_subtotal),
  ]);

  const pickingCards = pickings.map((picking) => {
    const rows = picking.moves.map((move) => [
      move.product_id.name,
      move.product_uom_qty,
    ]);

    return (
      <Col xs={12} lg={6} key={picking.mid} className="mb-3">
        <Card className="shadow-sm">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <span><strong>{picking.name}</strong> ({picking.state})</span>
            <small className="text-muted">Scheduled date: <b>{formatDate(picking.scheduled_date)}</b></small>
          </Card.Header>
          <Card.Body className="p-0">
            <DataTable
              headers={['Product', 'Quantity']}
              rows={rows}
            />
            <div className="p-2 px-3 bg-light border-top">
              <small className="text-muted">
                {kind === 'po' ? 'Destination' : 'Source'}:<b> {kind === 'po' ? picking.location_dest_id.name : picking.location_id.name }</b>
              </small>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  });

  return (
    <div className="page-container">
      <div className="mb-3">
        <BackButton fallbackUrl={kind === 'po' ? '/agent/po' : '/agent/so'}>
          &larr; Back to {kind === 'po' ? 'purchase' : 'sales'} orders
        </BackButton>
      </div>
      <PageHeader
        title={order.name}
        description={`${kind === 'po' ? 'Purchase' : 'Sales'} order details with lines and related pickings.`}
      />
      <Row className="g-3">
        <Col lg={5}>
          <Card className="shadow-sm h-100">
            <Card.Header>Information</Card.Header>
            <ListGroup variant="flush">
              <ListGroup.Item><strong>{kind === 'po' ? 'Purchaser' : 'Salesperson'}:</strong> {order.user_id?.name}</ListGroup.Item>
              <ListGroup.Item><strong>Order date:</strong> {formatDate(order.date_order)}</ListGroup.Item>
              <ListGroup.Item><strong>Total:</strong> {formatCurrency(order.amount_total)}</ListGroup.Item>
              <ListGroup.Item><strong>{kind === 'po' ? 'Deliver to' : 'Warehouse'}:</strong> { kind == 'po' ? order.picking_type_id?.name : order.warehouse_id?.name}</ListGroup.Item>
              <ListGroup.Item><strong>Incoterm:</strong> {order.incoterm_id?.name || '-'}</ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
        <Col lg={7}>
          <Card className="shadow-sm">
            <Card.Header>Lines</Card.Header>
            <Card.Body className="p-0">
              <DataTable
                headers={['Product', 'Description', 'Quantity', 'Unit price', 'Subtotal']}
                rows={lineRows}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} className="mt-4">
          <h5>Related pickings</h5>
        </Col>
        {pickingCards.length > 0 ? pickingCards : (
          <Col xs={3}>
            <Alert variant="warning" className="mb-0">No pickings yet.</Alert>
          </Col>
        )}
      </Row>
    </div>
  );
}
